// src/components/shell/Sidebar/BucketSwitcher.tsx
// Top-of-rail (and top-of-drawer) widget that displays the active bucket and,
// for users with access to more than one bucket, opens a popover to switch.
//
// Active bucket is derived from the current mode via resolveActiveBucket.
// Selecting a bucket calls setMode(getDefaultModeForBucket(bucket)).
import { createPortal } from "react-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAnchorEntitlements } from "@/contexts/AnchorEntitlementsContext";
import type { PrimaryMode } from "@/components/shell/modes/modesRegistry";
import {
  getAccessibleBuckets,
  getBucketById,
  getDefaultModeForBucket,
  resolveActiveBucket,
  type BucketRegistryEntry,
} from "@/components/shell/modes/bucketsRegistry";
import { railPopoverPanelStyle, useRailPopover } from "@/components/shell/popovers/useRailPopover";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import {
  FIREFLY_BUCKET_SWITCHER_TARGET_ID,
  fireflyBucketNavTargetId,
} from "@/lib/firefly/fireflyBucketNavTargets";

export type BucketSwitcherProps = {
  expanded: boolean;
  mode: PrimaryMode;
  setMode: (m: PrimaryMode) => void;
  /** Closes the mobile drawer after a switch (no-op on rail/desktop). */
  onAfterAction?: () => void;
};

export function BucketSwitcher({ expanded, mode, setMode, onAfterAction }: BucketSwitcherProps) {
  const auth = useAuth();
  const { anchors } = useAnchorEntitlements();
  const accessibleBuckets = getAccessibleBuckets(auth, anchors);
  const activeBucketId = resolveActiveBucket(mode, auth, anchors);

  // Hidden entirely if the user has no accessible bucket — should not happen
  // when the user is inside the workspace shell, but defensive.
  if (!activeBucketId || accessibleBuckets.length === 0) return null;

  const activeBucket = getBucketById(activeBucketId);
  const isMultiBucket = accessibleBuckets.length > 1;

  if (!isMultiBucket) {
    return (
      <div className="px-2 pt-2">
        <SingleBucketLabel expanded={expanded} bucket={activeBucket} />
      </div>
    );
  }

  return (
    <div className="px-2 pt-2">
      <MultiBucketTrigger
        expanded={expanded}
        activeBucket={activeBucket}
        buckets={accessibleBuckets}
        onPick={(b) => {
          const next = getDefaultModeForBucket(b.id, auth, anchors);
          if (next) setMode(next);
          onAfterAction?.();
        }}
      />
    </div>
  );
}

/** Non-interactive label row used when the user only has access to one bucket. */
function SingleBucketLabel({ expanded, bucket }: { expanded: boolean; bucket: BucketRegistryEntry }) {
  // Collapsed rail: glyph chip only (no teal plate — plate mis-centers the icon).
  if (!expanded) {
    return (
      <div
        className="flex justify-center py-1"
        aria-label={`Current workspace: ${bucket.label}`}
        title={`Workspace: ${bucket.label}`}
      >
        <span
          aria-hidden
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-teal-600 text-[11px] font-semibold text-white"
        >
          {bucket.glyph}
        </span>
      </div>
    );
  }
  return (
    <div
      className={[
        "flex items-center gap-3 rounded-xl px-2.5 py-2 text-left text-xs",
        "border border-teal-300/80 bg-teal-50/70 text-teal-900",
        "dark:border-teal-700/70 dark:bg-teal-950/35 dark:text-teal-100",
      ].join(" ")}
      aria-label={`Current workspace: ${bucket.label}`}
    >
      <span
        aria-hidden
        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-teal-600 text-[11px] font-semibold text-white"
      >
        {bucket.glyph}
      </span>
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="text-[10px] uppercase tracking-wide text-teal-700/80 dark:text-teal-300/80">
          Workspace
        </span>
        <span className="truncate text-sm font-semibold">{bucket.label}</span>
      </span>
    </div>
  );
}

type MultiBucketTriggerProps = {
  expanded: boolean;
  activeBucket: BucketRegistryEntry;
  buckets: BucketRegistryEntry[];
  onPick: (bucket: BucketRegistryEntry) => void;
};

function MultiBucketTrigger({ expanded, activeBucket, buckets, onPick }: MultiBucketTriggerProps) {
  const { open, toggle, setOpen, triggerRef, panelRef, position } = useRailPopover();

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Workspace: ${activeBucket.label}`}
        title={expanded ? undefined : `Workspace: ${activeBucket.label}`}
        {...{ [FIREFLY_TARGET_ATTR]: FIREFLY_BUCKET_SWITCHER_TARGET_ID }}
        className={[
          "w-full flex items-center text-left text-sm transition-colors",
          expanded
            ? [
                "gap-3 rounded-xl px-2.5 py-2",
                "border border-teal-300/80 bg-teal-50/70 text-teal-900",
                "hover:bg-teal-100/80 dark:border-teal-700/70 dark:bg-teal-950/35 dark:text-teal-100 dark:hover:bg-teal-950/55",
                open ? "ring-2 ring-teal-500/50" : "",
              ].join(" ")
            : "justify-center py-1 text-teal-900 dark:text-teal-100",
        ].join(" ")}
      >
        <span
          aria-hidden
          className={[
            "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-teal-600 text-[11px] font-semibold text-white",
            !expanded && open ? "ring-2 ring-teal-500/50" : "",
          ].join(" ")}
        >
          {activeBucket.glyph}
        </span>
        {expanded ? (
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="text-[10px] uppercase tracking-wide text-teal-700/80 dark:text-teal-300/80">
              Workspace
            </span>
            <span className="truncate font-semibold">{activeBucket.label}</span>
          </span>
        ) : null}
      </button>
      {open
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label="Switch workspace"
              className="rounded-2xl border border-teal-200 bg-white text-gray-900 shadow-2xl dark:border-teal-800 dark:bg-gray-900 dark:text-gray-100"
              style={railPopoverPanelStyle(position)}
            >
              <div className="px-4 py-3 border-b border-teal-100 dark:border-teal-900">
                <div className="text-[10px] uppercase tracking-wide text-teal-700 dark:text-teal-300">
                  Workspace
                </div>
                <div className="mt-0.5 text-sm font-semibold">{activeBucket.label}</div>
              </div>
              <ul className="py-2 px-2 space-y-1">
                {buckets.map((b) => {
                  const isActive = b.id === activeBucket.id;
                  return (
                    <li key={b.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setOpen(false);
                          if (!isActive) onPick(b);
                        }}
                        aria-current={isActive ? "true" : undefined}
                        {...{ [FIREFLY_TARGET_ATTR]: fireflyBucketNavTargetId(b.id) }}
                        className={[
                          "w-full flex items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm transition-colors",
                          isActive
                            ? "bg-teal-50 text-teal-900 font-semibold dark:bg-teal-950/40 dark:text-teal-100"
                            : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800/70",
                        ].join(" ")}
                      >
                        <span
                          aria-hidden
                          className={[
                            "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-[11px] font-semibold",
                            isActive
                              ? "bg-teal-600 text-white"
                              : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
                          ].join(" ")}
                        >
                          {b.glyph}
                        </span>
                        <span className="truncate">{b.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
